import { useState } from 'react';
import type { CwConnection, UpdateCwConnectionInput } from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { useTestCwConnection, useUpdateCwConnection } from './api.js';

interface ConnectionEditorProps {
  connection: CwConnection;
  onClose: () => void;
}

const SECRET_PLACEHOLDER = '••••••••';

function labelClass(): string {
  return 'mb-1 text-xs font-medium uppercase tracking-wide text-slate-500';
}

function inputClass(): string {
  return 'rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';
}

/**
 * Admin form for editing the ConnectWise connection. Plain fields are pre-filled
 * from the current effective configuration; secrets are write-only (we only show
 * whether one is stored). Only changed fields are sent, using the tri-state
 * contract (`undefined` = unchanged, `null` = clear, value = set).
 */
export function ConnectionEditor({ connection, onClose }: ConnectionEditorProps): JSX.Element {
  const updateMutation = useUpdateCwConnection();
  const testMutation = useTestCwConnection();

  const [siteUrl, setSiteUrl] = useState(connection.siteUrl ?? '');
  const [companyId, setCompanyId] = useState(connection.companyId ?? '');
  const [clientId, setClientId] = useState(connection.clientId ?? '');
  const [lookbackDays, setLookbackDays] = useState(String(connection.lookbackDays));
  const [publicKey, setPublicKey] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [clearPublicKey, setClearPublicKey] = useState(false);
  const [clearPrivateKey, setClearPrivateKey] = useState(false);

  const canStoreSecrets = connection.canStoreSecrets;

  const resolvePlain = (next: string, original: string | null): string | null | undefined => {
    const trimmed = next.trim();
    const base = original ?? '';
    if (trimmed === base) return undefined;
    return trimmed === '' ? null : trimmed;
  };

  const resolveSecret = (next: string, clear: boolean): string | null | undefined => {
    if (clear) return null;
    const trimmed = next.trim();
    return trimmed === '' ? undefined : trimmed;
  };

  const buildInput = (): UpdateCwConnectionInput => {
    const input: UpdateCwConnectionInput = {};
    const site = resolvePlain(siteUrl, connection.siteUrl);
    if (site !== undefined) input.siteUrl = site;
    const company = resolvePlain(companyId, connection.companyId);
    if (company !== undefined) input.companyId = company;
    const client = resolvePlain(clientId, connection.clientId);
    if (client !== undefined) input.clientId = client;

    const lookbackTrimmed = lookbackDays.trim();
    if (lookbackTrimmed === '') {
      if (connection.lookbackDays) input.lookbackDays = null;
    } else {
      const parsed = Number(lookbackTrimmed);
      if (Number.isFinite(parsed) && parsed !== connection.lookbackDays) {
        input.lookbackDays = parsed;
      }
    }

    if (canStoreSecrets) {
      const pub = resolveSecret(publicKey, clearPublicKey);
      if (pub !== undefined) input.publicKey = pub;
      const priv = resolveSecret(privateKey, clearPrivateKey);
      if (priv !== undefined) input.privateKey = priv;
    }
    return input;
  };

  const handleSave = (): void => {
    updateMutation.mutate(buildInput(), {
      onSuccess: () => {
        setPublicKey('');
        setPrivateKey('');
        setClearPublicKey(false);
        setClearPrivateKey(false);
        onClose();
      },
    });
  };

  const testResult = testMutation.data;

  return (
    <div className="mt-4 border-t border-slate-200 pt-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col text-sm">
          <span className={labelClass()}>Site URL</span>
          <input
            type="text"
            value={siteUrl}
            onChange={(e) => setSiteUrl(e.target.value)}
            placeholder="https://api-na.myconnectwise.net"
            className={inputClass()}
          />
        </label>
        <label className="flex flex-col text-sm">
          <span className={labelClass()}>Company ID</span>
          <input
            type="text"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            className={inputClass()}
          />
        </label>
        <label className="flex flex-col text-sm">
          <span className={labelClass()}>Client ID</span>
          <input
            type="text"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className={inputClass()}
          />
        </label>
        <label className="flex flex-col text-sm">
          <span className={labelClass()}>Look-back days</span>
          <input
            type="number"
            min={1}
            value={lookbackDays}
            onChange={(e) => setLookbackDays(e.target.value)}
            className={inputClass()}
          />
        </label>
        <label className="flex flex-col text-sm">
          <span className={labelClass()}>
            Public key {connection.hasPublicKey ? <Badge tone="green">stored</Badge> : null}
          </span>
          <input
            type="password"
            value={publicKey}
            onChange={(e) => setPublicKey(e.target.value)}
            disabled={!canStoreSecrets || clearPublicKey}
            placeholder={connection.hasPublicKey ? SECRET_PLACEHOLDER : ''}
            autoComplete="new-password"
            className={inputClass()}
          />
          {connection.hasPublicKey ? (
            <label className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500">
              <input
                type="checkbox"
                checked={clearPublicKey}
                onChange={(e) => setClearPublicKey(e.target.checked)}
              />
              Clear stored public key
            </label>
          ) : null}
        </label>
        <label className="flex flex-col text-sm">
          <span className={labelClass()}>
            Private key {connection.hasPrivateKey ? <Badge tone="green">stored</Badge> : null}
          </span>
          <input
            type="password"
            value={privateKey}
            onChange={(e) => setPrivateKey(e.target.value)}
            disabled={!canStoreSecrets || clearPrivateKey}
            placeholder={connection.hasPrivateKey ? SECRET_PLACEHOLDER : ''}
            autoComplete="new-password"
            className={inputClass()}
          />
          {connection.hasPrivateKey ? (
            <label className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500">
              <input
                type="checkbox"
                checked={clearPrivateKey}
                onChange={(e) => setClearPrivateKey(e.target.checked)}
              />
              Clear stored private key
            </label>
          ) : null}
        </label>
      </div>

      {!canStoreSecrets ? (
        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
          Credential storage is disabled. Set the <code>CW_CONFIG_ENCRYPTION_KEY</code> environment
          variable on the server to enter API keys from here.
        </p>
      ) : null}

      {updateMutation.isError ? (
        <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
          Failed to save connection settings.
        </p>
      ) : null}

      {testResult ? (
        <p
          className={`mt-3 rounded-lg px-3 py-2 text-sm ${
            testResult.ok ? 'bg-green-50 text-green-700' : 'bg-rose-50 text-rose-700'
          }`}
        >
          {testResult.message}
        </p>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button onClick={handleSave} isLoading={updateMutation.isPending}>
          Save
        </Button>
        <Button
          variant="secondary"
          onClick={() => testMutation.mutate()}
          isLoading={testMutation.isPending}
          disabled={!connection.configured}
        >
          Test connection
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
